import i18n from "../i18n";
import type { ContentBrandFilterValue } from "./programStats/metricsDetail/contentBrandFilter";
import type { MetricsReportPeriod } from "./metricsReportPeriod";

/** ISO weekday: 1 = Monday … 7 = Sunday */
export type MetricsReportDayOfWeek = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type MetricsReportScheduleDto = {
  id: string;
  companyId: string;
  podcastId: string;
  programId: string | null;
  enabled: boolean;
  recipients: string[];
  dayOfWeek: MetricsReportDayOfWeek;
  contentBrandFilter: ContentBrandFilterValue;
  reportPeriod: MetricsReportPeriod;
  updatedAt: string;
};

export type MetricsReportScheduleUpsertPayload = {
  enabled: boolean;
  recipients: string[];
  dayOfWeek: MetricsReportDayOfWeek;
  contentBrandFilter: ContentBrandFilterValue;
  reportPeriod: MetricsReportPeriod;
};

export type MetricsReportScheduleListItemDto = MetricsReportScheduleDto & {
  scope: "podcast" | "program";
  podcastTitle: string;
  programTitle: string | null;
};

/** @deprecated Legacy localStorage shape — used only for one-time migration. */
export type LegacyMetricsReportScheduleConfig = {
  enabled: boolean;
  recipients: string[];
  dayOfWeek: MetricsReportDayOfWeek;
  variant: "podcast" | "program";
  entityId: string;
  contentBrandFilter: ContentBrandFilterValue;
};

export const METRICS_REPORT_WEEKDAYS: {
  value: MetricsReportDayOfWeek;
  label: string;
}[] = (
  [1, 2, 3, 4, 5, 6, 7] as MetricsReportDayOfWeek[]
).map((value) => ({
  value,
  get label() {
    return i18n.t(`miContenido:weekdays.${value}`);
  },
}));

const STORAGE_PREFIX = "mi-contenido-metrics-report-schedule";

function legacyStorageKey(
  variant: "podcast" | "program",
  entityId: string,
): string {
  return `${STORAGE_PREFIX}:${variant}:${entityId}`;
}

/** Read legacy localStorage config for one-time migration. */
export function readLegacyMetricsReportSchedule(
  variant: "podcast" | "program",
  entityId: string,
): LegacyMetricsReportScheduleConfig | null {
  if (typeof window === "undefined" || !entityId) return null;
  try {
    const raw = window.localStorage.getItem(legacyStorageKey(variant, entityId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LegacyMetricsReportScheduleConfig;
    if (parsed.variant !== variant || parsed.entityId !== entityId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearLegacyMetricsReportSchedule(
  variant: "podcast" | "program",
  entityId: string,
): void {
  if (typeof window === "undefined" || !entityId) return;
  window.localStorage.removeItem(legacyStorageKey(variant, entityId));
}

export function parseRecipientsInput(input: string): string[] {
  return input
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function formatRecipientsInput(recipients: string[]): string {
  return recipients.join(", ");
}

export function legacyConfigToUpsertPayload(
  legacy: LegacyMetricsReportScheduleConfig,
): MetricsReportScheduleUpsertPayload {
  return {
    enabled: legacy.enabled,
    recipients: legacy.recipients,
    dayOfWeek: legacy.dayOfWeek,
    contentBrandFilter: "all",
    reportPeriod: "last_week",
  };
}
